import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientPortalLayout from "./ClientPortalLayout";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  AccountBalance as AccountingIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  Description as StatementIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";
import { useSnackbar } from "@codejq/react-simple-snackbar";
import { useDispatch } from "react-redux";
import { getAccountingPortalData } from "../../actions/clientPortalActions";
import { format } from "date-fns";

const AccountingClientPortal = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation("crm");
  const [openSnackbar] = useSnackbar();
  const dispatch = useDispatch();
  
  const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    return lang.split('-')[0];
  };
  
  const currentLang = normalizeLanguage(i18n.language);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});

  const fetchAccountingData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dispatch(getAccountingPortalData());
      setInvoices(data.invoices || []);
      setPayments(data.payments || []);
      setSummary(data.summary || {});
    } catch (error) {
      console.error("Error fetching accounting data:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAccountingData();
  }, [fetchAccountingData]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const modules = [
    {
      name: 'accounting',
      label: 'Accounting',
      icon: <AccountingIcon />,
      path: '/client-portal/accounting'
    }
  ];

  if (loading) {
    return (
      <ClientPortalLayout modules={modules} currentModule="accounting">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </ClientPortalLayout>
    );
  }

  return (
    <ClientPortalLayout modules={modules} currentModule="accounting">
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          <AccountingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('accounting_portal')}
        </Typography>

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label={t('overview')} icon={<ReportIcon />} iconPosition="start" />
          <Tab label={t('invoices')} icon={<InvoiceIcon />} iconPosition="start" />
          <Tab label={t('payments')} icon={<PaymentIcon />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('total_outstanding')}</Typography>
                  <Typography variant="h4">${summary.totalOutstanding?.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('total_paid')}</Typography>
                  <Typography variant="h4">${summary.totalPaid?.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>{t('overdue')}</Typography>
                  <Typography variant="h4">${summary.overdueAmount?.toLocaleString()}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>{t('recent_invoices')}</Typography>
                <List>
                  {invoices.map((invoice) => (
                    <React.Fragment key={invoice._id}>
                      <ListItem>
                        <ListItemText
                          primary={invoice.invoiceNumber}
                          secondary={`${invoice.description} • $${invoice.amount.toLocaleString()}`}
                        />
                        <Chip label={invoice.status} color={getStatusColor(invoice.status)} size="small" />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('invoices')}</Typography>
            {invoices.length === 0 ? (
              <Alert severity="info">{t('no_invoices_found')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('invoice_number')}</TableCell>
                      <TableCell>{t('description')}</TableCell>
                      <TableCell>{t('amount')}</TableCell>
                      <TableCell>{t('due_date')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice._id}>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell>{invoice.description}</TableCell>
                        <TableCell>${invoice.amount.toLocaleString()}</TableCell>
                        <TableCell>{format(new Date(invoice.dueDate), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>
                          <Chip label={invoice.status} color={getStatusColor(invoice.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>{t('payment_history')}</Typography>
            {payments.length === 0 ? (
              <Alert severity="info">{t('no_payments_found')}</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('payment_number')}</TableCell>
                      <TableCell>{t('invoice')}</TableCell>
                      <TableCell>{t('amount')}</TableCell>
                      <TableCell>{t('method')}</TableCell>
                      <TableCell>{t('date')}</TableCell>
                      <TableCell>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell>{payment.paymentNumber}</TableCell>
                        <TableCell>{payment.invoiceNumber}</TableCell>
                        <TableCell>${payment.amount.toLocaleString()}</TableCell>
                        <TableCell>{payment.paymentMethod}</TableCell>
                        <TableCell>{format(new Date(payment.paymentDate), 'MMM dd, yyyy')}</TableCell>
                        <TableCell>
                          <Chip label={payment.status} color={getStatusColor(payment.status)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Box>
    </ClientPortalLayout>
  );
};

export default AccountingClientPortal;
